import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { localDateString, formatInTz } from '../lib/timezones';
import { fmtTs } from '../lib/globalTz';
import { exportDomAsHtml, exportDomAsPdf, slugify } from '../lib/exportDomReport';

type Period = 'daily' | 'monthly' | 'quarterly' | 'yearly';
type ViewMode = 'owners' | 'individual';

const PERIODS: { id: Period; label: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'yearly', label: 'Yearly' },
];

interface OwnerRow {
  owner: string;
  totalBreached: number;
  criticalBreaches: number;
  highBreaches: number;
  mediumBreaches: number;
  lowBreaches: number;
  informationalBreaches: number;
  mttdBreaches: number;
  mttrBreaches: number;
  avgMttdMinutes: number | null;
  avgMttrMinutes: number | null;
}

interface SocKpiPayload {
  ok: true;
  customerId: string;
  customerName: string;
  logoUrl?: string;
  from: string;
  to: string;
  generatedAt: string;
  totalOutsideSla: number;
  ownersWithBreaches: number;
  owners: OwnerRow[];
}

interface IncidentDetailRow {
  timeGenerated: string;
  incidentNumber: string;
  title: string;
  severity: string;
  status: string;
  createdTime: string;
  firstResponseTime: string | null;
  closedTime: string | null;
  owner: string;
  mttdMinutes: number | null;
  mttrMinutes: number | null;
  mttdStatus: string;
  mttrStatus: string;
  overallSla: string;
  incidentUrl: string;
}

interface IncidentDetailPayload {
  ok: true;
  customerId: string;
  customerName: string;
  logoUrl?: string;
  from: string;
  to: string;
  generatedAt: string;
  thresholds: { severity: string; ttaMinutes: number; ttrMinutes: number }[];
  incidents: IncidentDetailRow[];
}

interface OwnerGroup {
  owner: string;
  incidents: IncidentDetailRow[];
  totalBreached: number;
  criticalBreaches: number;
  highBreaches: number;
  mediumBreaches: number;
  lowBreaches: number;
  informationalBreaches: number;
  mttdBreaches: number;
  mttrBreaches: number;
  avgMttdMinutes: number | null;
  avgMttrMinutes: number | null;
}

/** Groups a raw incident list by owner and derives the same accountability
 *  stats the owner-summary KQL computes server-side, so the "Individual
 *  Reports" view works uniformly for every period (including Daily, which
 *  has no separate owner-summary endpoint of its own). */
function groupIncidentsByOwner(incidents: IncidentDetailRow[]): OwnerGroup[] {
  const map = new Map<string, IncidentDetailRow[]>();
  for (const inc of incidents) {
    const key = inc.owner || 'Unassigned';
    const arr = map.get(key);
    if (arr) arr.push(inc); else map.set(key, [inc]);
  }
  const avg = (values: number[]): number | null => {
    if (values.length === 0) return null;
    return Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10;
  };
  const groups: OwnerGroup[] = [];
  for (const [owner, incs] of map.entries()) {
    const breached = incs.filter((i) => i.overallSla === 'Outside SLA');
    const bySev = (sev: string) => breached.filter((i) => i.severity === sev).length;
    const mttdBreachedIncs = incs.filter((i) => i.mttdStatus === 'Outside SLA');
    const mttrBreachedIncs = incs.filter((i) => i.mttrStatus === 'Outside SLA');
    groups.push({
      owner,
      incidents: incs,
      totalBreached: breached.length,
      criticalBreaches: bySev('Critical'),
      highBreaches: bySev('High'),
      mediumBreaches: bySev('Medium'),
      lowBreaches: bySev('Low'),
      informationalBreaches: bySev('Informational'),
      mttdBreaches: mttdBreachedIncs.length,
      mttrBreaches: mttrBreachedIncs.length,
      avgMttdMinutes: avg(mttdBreachedIncs.map((i) => i.mttdMinutes).filter((v): v is number => v !== null)),
      avgMttrMinutes: avg(mttrBreachedIncs.map((i) => i.mttrMinutes).filter((v): v is number => v !== null)),
    });
  }
  return groups.sort((a, b) => b.totalBreached - a.totalBreached);
}

function ymd(d: Date, tz: string): string {
  return localDateString(d, tz);
}

/** UTC instant corresponding to Y-M-D 00:00:00 local wall-clock time in IANA timezone `tz`. */
function zonedMidnightUtc(y: number, m: number, d: number, tz: string): Date {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return new Date();
  const guess = Date.UTC(y, m - 1, d, 0, 0, 0);
  if (!Number.isFinite(guess)) return new Date();
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(guess));
  const get = (type: string) => Number(parts.find((x) => x.type === type)?.value ?? '0');
  const asUtcOfLocal = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return new Date(guess - (asUtcOfLocal - guess));
}

/** Formats an exclusive end-of-range instant as the last displayed moment of the prior day, in `tz`. */
function fmtRangeEnd(value: string, tz: string): string {
  const t0 = new Date(value).getTime();
  if (!Number.isFinite(t0)) return '–';
  return formatInTz(new Date(t0 - 60000), tz, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Computes the [from, to) window for a period, anchored to `anchor`
 *  (a "YYYY-MM-DD" or "YYYY-MM" or "YYYY" string depending on period),
 *  interpreted at midnight in the customer's timezone. */
function computeRange(period: Period, anchor: string, tz: string): { from: string; to: string; label: string } {
  if (period === 'daily') {
    const [y, m, d] = anchor.split('-').map(Number);
    const from = zonedMidnightUtc(y!, m!, d!, tz);
    const to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString(), label: anchor };
  }
  if (period === 'monthly') {
    const [y, m] = anchor.split('-').map(Number);
    const from = zonedMidnightUtc(y!, m!, 1, tz);
    const to = zonedMidnightUtc(m === 12 ? y! + 1 : y!, m === 12 ? 1 : m! + 1, 1, tz);
    const label = new Date(Date.UTC(y!, m! - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    return { from: from.toISOString(), to: to.toISOString(), label };
  }
  if (period === 'quarterly') {
    const [y, q] = anchor.split('-Q').map(Number);
    const startMonth = (q! - 1) * 3;
    const from = zonedMidnightUtc(y!, startMonth + 1, 1, tz);
    const to = zonedMidnightUtc(startMonth + 3 === 12 ? y! + 1 : y!, startMonth + 3 === 12 ? 1 : startMonth + 4, 1, tz);
    return { from: from.toISOString(), to: to.toISOString(), label: `Q${q} ${y}` };
  }
  // yearly
  const y = Number(anchor);
  const from = zonedMidnightUtc(y, 1, 1, tz);
  const to = zonedMidnightUtc(y + 1, 1, 1, tz);
  return { from: from.toISOString(), to: to.toISOString(), label: String(y) };
}

function defaultAnchor(period: Period, tz: string): string {
  const today = ymd(new Date(), tz);
  const [y, m, d] = today.split('-').map(Number);
  if (period === 'daily') {
    const yest = new Date(Date.UTC(y!, m! - 1, d! - 1));
    return yest.toISOString().slice(0, 10);
  }
  if (period === 'monthly') {
    const prevMonth = m === 1 ? 12 : m! - 1;
    const prevYear = m === 1 ? y! - 1 : y!;
    return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
  }
  if (period === 'quarterly') {
    const q = Math.floor((m! - 1) / 3) + 1;
    const prevQ = q === 1 ? 4 : q - 1;
    const prevY = q === 1 ? y! - 1 : y!;
    return `${prevY}-Q${prevQ}`;
  }
  return String(y);
}

function fmtMinutes(mins: number | null): string {
  if (mins === null || !Number.isFinite(mins)) return '–';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

/** Report-card palette, switched by the app's site-wide dark/light toggle.
 *  This card is a branded, fixed-layout artifact (mirrors the customer's
 *  Logic-App-generated email report) rather than themed Tailwind classes, so
 *  it needs its own explicit light/dark color pairs instead of `dark:` utility
 *  classes. */
function getPalette(darkMode: boolean) {
  return darkMode
    ? {
        pageBg: '#f3f4f6',
        cardBg: '#1b1b2b',
        cardBg2: '#232336',
        orange: '#f28c28',
        red: '#e0526b',
        blue: '#93c5fd',
        headerLabel: '#9ca3af',
        customerName: '#d1d5db',
        periodText: '#9ca3af',
        tileLabel: '#9ca3af',
        bannerBg: '#4b4526',
        bannerText: '#f2d98a',
        ownerText: '#e5e7eb',
        neutralCell: '#e5e7eb',
        infoCell: '#9ca3af',
        footerNoteBg: '#1f3a5f',
        footerNoteText: '#cbd5e1',
        copyrightText: '#6b7280',
        copyrightBorder: '#2f2f45',
        logoCardBg: '#ffffff',
        openStatus: '#fb923c',
      }
    : {
        pageBg: '#e5e7eb',
        cardBg: '#ffffff',
        cardBg2: '#f3f4f6',
        orange: '#c2650f',
        red: '#c23a54',
        blue: '#1e40af',
        headerLabel: '#6b7280',
        customerName: '#374151',
        periodText: '#6b7280',
        tileLabel: '#6b7280',
        bannerBg: '#fef3c7',
        bannerText: '#92400e',
        ownerText: '#1f2937',
        neutralCell: '#1f2937',
        infoCell: '#6b7280',
        footerNoteBg: '#dbeafe',
        footerNoteText: '1e40af',
        copyrightText: '#6b7280',
        copyrightBorder: '#e5e7eb',
        logoCardBg: '#f9fafb',
        openStatus: '#c2650f',
      };
}

type Palette = ReturnType<typeof getPalette>;

function statusColor(pal: Palette, status: string): string {
  if (status === 'Outside SLA') return pal.red;
  if (status === 'Open') return pal.openStatus;
  if (status === 'Within SLA') return pal.neutralCell;
  return pal.infoCell;
}

function IncidentTable({ pal, incidents }: { pal: Palette; incidents: IncidentDetailRow[] }) {
  if (incidents.length === 0) {
    return <div style={{ color: pal.tileLabel, fontSize: 13, padding: 24, textAlign: 'center' }}>No incidents in this period.</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
      <thead>
        <tr style={{ background: '#1e5fa8' }}>
          {['Time Generated', 'Incident #', 'Title', 'Severity', 'Status', 'Created Time', 'First Response', 'Closed Time', 'Owner', 'MTTD', 'MTTR', 'MTTD Status', 'MTTR Status', 'Overall SLA'].map((h) => (
            <th key={h} style={{ color: '#fff', padding: '8px 6px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {incidents.map((inc, i) => (
          <tr key={`${inc.incidentNumber}-${inc.timeGenerated}`} style={{ background: i % 2 === 0 ? pal.cardBg : pal.cardBg2 }}>
            <td style={{ color: pal.neutralCell, padding: '6px', whiteSpace: 'nowrap' }}>{fmtTs(inc.timeGenerated)}</td>
            <td style={{ color: pal.neutralCell, padding: '6px' }}>{inc.incidentUrl ? (<a href={inc.incidentUrl} target="_blank" rel="noopener noreferrer" style={{ color: pal.orange, fontWeight: 700, textDecoration: 'underline' }}>{inc.incidentNumber}</a>) : inc.incidentNumber}</td>
            <td style={{ color: pal.ownerText, padding: '6px', fontWeight: 600, maxWidth: 220 }}>{inc.title}</td>
            <td style={{ color: pal.neutralCell, padding: '6px' }}>{inc.severity}</td>
            <td style={{ color: pal.neutralCell, padding: '6px' }}>{inc.status}</td>
            <td style={{ color: pal.neutralCell, padding: '6px', whiteSpace: 'nowrap' }}>{fmtTs(inc.createdTime)}</td>
            <td style={{ color: pal.neutralCell, padding: '6px', whiteSpace: 'nowrap' }}>{inc.firstResponseTime ? fmtTs(inc.firstResponseTime) : '–'}</td>
            <td style={{ color: pal.neutralCell, padding: '6px', whiteSpace: 'nowrap' }}>{inc.closedTime ? fmtTs(inc.closedTime) : '–'}</td>
            <td style={{ color: pal.neutralCell, padding: '6px' }}>{inc.owner}</td>
            <td style={{ color: pal.neutralCell, padding: '6px', textAlign: 'center' }}>{inc.mttdMinutes ?? '–'}</td>
            <td style={{ color: pal.neutralCell, padding: '6px', textAlign: 'center' }}>{inc.mttrMinutes ?? '–'}</td>
            <td style={{ color: statusColor(pal, inc.mttdStatus), padding: '6px', fontWeight: 700 }}>{inc.mttdStatus}</td>
            <td style={{ color: statusColor(pal, inc.mttrStatus), padding: '6px', fontWeight: 700 }}>{inc.mttrStatus}</td>
            <td style={{ color: statusColor(pal, inc.overallSla), padding: '6px', fontWeight: 700 }}>{inc.overallSla}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IndividualReportCard({
  pal, group, logoUrl, customerName, periodLabel, from, to, generatedAt, customerTz,
}: {
  pal: Palette; group: OwnerGroup; logoUrl?: string; customerName: string; periodLabel: string;
  from: string; to: string; generatedAt: string; customerTz: string;
}) {
  const severityTiles: [string, number, string][] = [
    ['Critical', group.criticalBreaches, '#f87171'],
    ['High', group.highBreaches, '#fb923c'],
    ['Medium', group.mediumBreaches, '#d97706'],
    ['Low', group.lowBreaches, '#3b82f6'],
    ['Info', group.informationalBreaches, pal.infoCell],
  ];
  return (
    <div style={{ background: pal.cardBg, borderRadius: 12, overflow: 'hidden', fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 8px' }}>
        {logoUrl ? (
          <img src={logoUrl} alt={customerName} style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} />
        ) : <div />}
        <img src="/kloudynet-logo.png?v=2" alt="Kloudynet" style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      </div>

      <div style={{ textAlign: 'center', padding: '8px 24px 24px' }}>
        <div style={{ color: pal.headerLabel, fontSize: 13, fontWeight: 700, letterSpacing: 3 }}>INDIVIDUAL SOC KPI REPORT</div>
        <div style={{ color: pal.orange, fontSize: 22, fontWeight: 800, marginTop: 6 }}>SLA Breach Details - {periodLabel}</div>
        <div style={{ color: pal.customerName, fontSize: 14, marginTop: 8 }}>{customerName.toUpperCase()}</div>
        <div style={{ color: pal.periodText, fontSize: 12, marginTop: 4 }}>
          Report Period: {formatInTz(from, customerTz, { dateStyle: 'medium', timeStyle: 'short' })} - {fmtRangeEnd(to, customerTz)}
        </div>
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg, ${pal.orange}, ${pal.red})` }} />

      <div style={{ margin: '20px 24px 0', background: pal.cardBg2, borderRadius: 8, padding: '14px 20px' }}>
        <div style={{ color: pal.tileLabel, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>ANALYST / OWNER</div>
        <div style={{ color: pal.ownerText, fontSize: 18, fontWeight: 700, marginTop: 4 }}>{group.owner}</div>
      </div>

      <div style={{ display: 'flex', gap: 16, padding: '16px 24px' }}>
        <div style={{ flex: 1, background: pal.cardBg2, borderLeft: `4px solid ${pal.red}`, borderRadius: 8, padding: '16px 18px' }}>
          <div style={{ color: pal.red, fontSize: 28, fontWeight: 800 }}>{group.totalBreached}</div>
          <div style={{ color: pal.tileLabel, fontSize: 11, marginTop: 4 }}>Total Breaches</div>
        </div>
        <div style={{ flex: 1, background: pal.cardBg2, borderLeft: `4px solid ${pal.orange}`, borderRadius: 8, padding: '16px 18px' }}>
          <div style={{ color: pal.orange, fontSize: 22, fontWeight: 800 }}>{fmtMinutes(group.avgMttdMinutes)}</div>
          <div style={{ color: pal.tileLabel, fontSize: 11, marginTop: 4 }}>Avg MTTD</div>
        </div>
        <div style={{ flex: 1, background: pal.cardBg2, borderLeft: `4px solid ${pal.orange}`, borderRadius: 8, padding: '16px 18px' }}>
          <div style={{ color: pal.orange, fontSize: 22, fontWeight: 800 }}>{fmtMinutes(group.avgMttrMinutes)}</div>
          <div style={{ color: pal.tileLabel, fontSize: 11, marginTop: 4 }}>Avg MTTR</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24, padding: '0 24px 16px', flexWrap: 'wrap' }}>
        {severityTiles.map(([label, val, color]) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ color, fontSize: 18, fontWeight: 800 }}>{val}</div>
            <div style={{ color: pal.tileLabel, fontSize: 10 }}>{label}</div>
          </div>
        ))}
        <div style={{ width: 1, alignSelf: 'stretch', background: pal.copyrightBorder }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: pal.neutralCell, fontSize: 18, fontWeight: 800 }}>{group.mttdBreaches}</div>
          <div style={{ color: pal.tileLabel, fontSize: 10 }}>MTTD Breaches</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: pal.neutralCell, fontSize: 18, fontWeight: 800 }}>{group.mttrBreaches}</div>
          <div style={{ color: pal.tileLabel, fontSize: 10 }}>MTTR Breaches</div>
        </div>
      </div>

      <div style={{ margin: '0 24px 16px', background: pal.bannerBg, borderRadius: 6, padding: '10px 16px', color: pal.bannerText, fontSize: 12, fontWeight: 600 }}>
        ACTION REQUIRED: Please review each incident below. Click the Incident # to open it directly in Microsoft Sentinel.
      </div>

      <div style={{ margin: '0 24px 24px', overflowX: 'auto' }}>
        <IncidentTable pal={pal} incidents={group.incidents} />
      </div>

      <div style={{ textAlign: 'center', padding: '16px', borderTop: `1px solid ${pal.copyrightBorder}`, color: pal.copyrightText, fontSize: 11 }}>
        © {new Date(generatedAt).getFullYear()} {customerName} | Managed by Kloudynet Technologies Sdn Bhd
      </div>
    </div>
  );
}

export function SocKpiTab({ customerId: customerIdProp, customerName: customerNameProp, darkMode = false }: { customerId?: string; customerName?: string; darkMode?: boolean } = {}) {
  const { activeCustomerId, activeCustomer, customers } = useCustomers();
  const customerId = customerIdProp ?? activeCustomerId;
  const customer = customerIdProp ? customers.find((c) => c.id === customerIdProp) : activeCustomer;
  const customerTz = customer?.timezone ?? 'UTC';
  const displayCustomerName = customerNameProp ?? customer?.name ?? customerId ?? 'Customer';
  const pal = useMemo(() => getPalette(darkMode), [darkMode]);

  const [period, setPeriod] = useState<Period>('monthly');
  const [anchor, setAnchor] = useState<string>(() => defaultAnchor('monthly', customerTz));
  const [view, setView] = useState<ViewMode>('owners');
  const [selectedAnalyst, setSelectedAnalyst] = useState<string>('');
  const [reportBusy, setReportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const changePeriod = useCallback((p: Period) => {
    setPeriod(p);
    setAnchor(defaultAnchor(p, customerTz));
  }, [customerTz]);

  const { from, to, label: periodLabel } = useMemo(() => computeRange(period, anchor, customerTz), [period, anchor, customerTz]);
  const isDaily = period === 'daily';
  const hasCustomer = !!customerId && customerId !== 'default';
  // Individual Reports always needs the raw per-incident list (grouped client-side
  // by owner) regardless of period - Daily already fetches it for its own report.
  const needsIncidents = isDaily || view === 'individual';

  const ownerQuery = useQuery<SocKpiPayload, Error>({
    queryKey: ['soc-kpi', customerId, from, to],
    queryFn: () => apiFetch<SocKpiPayload>(`/soc-kpi/summary?customerId=${encodeURIComponent(customerId ?? '')}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&period=${encodeURIComponent(period)}&periodKey=${encodeURIComponent(anchor)}`),
    enabled: hasCustomer && !isDaily && view === 'owners',
  });

  const incidentQuery = useQuery<IncidentDetailPayload, Error>({
    queryKey: ['soc-kpi-incidents', customerId, from, to],
    queryFn: () => apiFetch<IncidentDetailPayload>(`/soc-kpi/incidents?customerId=${encodeURIComponent(customerId ?? '')}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&period=${encodeURIComponent(period)}&periodKey=${encodeURIComponent(anchor)}`),
    enabled: hasCustomer && needsIncidents,
  });

  const report = ownerQuery.data ?? null;
  const dailyReport = incidentQuery.data ?? null;
  const loading = needsIncidents ? (incidentQuery.isLoading || incidentQuery.isFetching) : (ownerQuery.isLoading || ownerQuery.isFetching);
  const error: string | null = !hasCustomer
    ? 'Select a specific customer to load the SOC KPI report. The MSSP "default" workspace is not supported here.'
    : (needsIncidents ? incidentQuery.error?.message : ownerQuery.error?.message) ?? null;

  const periodTitle = period === 'daily' ? 'Daily' : period === 'monthly' ? 'Monthly' : period === 'quarterly' ? 'Quarterly' : 'Yearly';
  const hasContent = needsIncidents ? !!dailyReport : !!report;

  const ownerGroups = useMemo(() => {
    if (view !== 'individual' || !dailyReport) return [];
    return groupIncidentsByOwner(dailyReport.incidents);
  }, [view, dailyReport]);
  const effectiveAnalyst = ownerGroups.some((g) => g.owner === selectedAnalyst) ? selectedAnalyst : (ownerGroups[0]?.owner ?? '');
  const selectedGroup = ownerGroups.find((g) => g.owner === effectiveAnalyst) ?? null;

  const exportOpts = useCallback(() => ({
    rootSelector: '.soc-kpi-report-root',
    title: `${displayCustomerName} - SOC KPI - ${periodLabel}`,
    fileNameBase: `kloudynet-soc-kpi-${slugify(displayCustomerName)}-${slugify(periodLabel)}`,
    notMountedError: 'SOC KPI report content not visible - load a report first.',
  }), [displayCustomerName, periodLabel]);

  async function downloadPdf() {
    if (!hasContent) return;
    setReportBusy(true);
    setExportError(null);
    try {
      const r = await exportDomAsPdf(exportOpts());
      if (!r.ok) setExportError(r.error ?? 'PDF export failed.');
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setReportBusy(false);
    }
  }

  async function downloadHtml() {
    if (!hasContent) return;
    setReportBusy(true);
    setExportError(null);
    try {
      const r = await exportDomAsHtml(exportOpts());
      if (!r.ok) setExportError(r.error ?? 'HTML export failed.');
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setReportBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="dr-no-print rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">SOC KPI</div>
            <h2 className="text-2xl font-black text-foreground">{view === 'individual' ? 'Individual SOC KPI Report' : isDaily ? 'Security Operations Report' : 'SLA Breach Summary'}</h2>
            <p className="text-sm text-muted-foreground max-w-3xl">{view === 'individual' ? 'Per-analyst SLA breach detail' : isDaily ? 'Per-incident SLA detail' : 'Owner-accountability view of SLA breaches'} for {displayCustomerName} - {periodTitle} period.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex gap-1 rounded-xl border border-border bg-background p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => changePeriod(p.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${period === p.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period === 'daily' && (
              <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            )}
            {period === 'monthly' && (
              <input type="month" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            )}
            {period === 'quarterly' && (
              <select value={anchor} onChange={(e) => setAnchor(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                {(() => {
                  const [ay] = anchor.split('-Q').map(Number);
                  const years = [ay! - 1, ay!, ay! + 1];
                  const opts: JSX.Element[] = [];
                  for (const yr of years) {
                    for (let q = 1; q <= 4; q++) opts.push(<option key={`${yr}-Q${q}`} value={`${yr}-Q${q}`}>{`Q${q} ${yr}`}</option>);
                  }
                  return opts;
                })()}
              </select>
            )}
            {period === 'yearly' && (
              <input type="number" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="w-24 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            )}
            {hasContent && (
              <>
                <button type="button" onClick={() => void downloadPdf()} disabled={reportBusy} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60">{reportBusy ? 'Preparing…' : 'Download PDF'}</button>
                <button type="button" onClick={() => void downloadHtml()} disabled={reportBusy} className="rounded-xl border border-border px-4 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">Download HTML</button>
              </>
            )}
          </div>
        </div>
        {hasCustomer && (
          <div className="mt-3 flex gap-1 rounded-xl border border-border bg-background p-1 w-fit">
            <button
              type="button"
              onClick={() => setView('owners')}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${view === 'owners' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              {isDaily ? 'Security Operations Report' : 'Owner Summary'}
            </button>
            <button
              type="button"
              onClick={() => setView('individual')}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${view === 'individual' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              Individual Reports
            </button>
          </div>
        )}
        {loading && <div className="mt-3 text-xs text-muted-foreground">Loading…</div>}
        {error && <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}
        {exportError && <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{exportError}</div>}
      </div>

      {!hasCustomer ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">Select a customer to load the SOC KPI report.</div>
      ) : view === 'individual' ? (
        dailyReport ? (
          <div className="soc-kpi-report-root" style={{ background: pal.pageBg, borderRadius: 16, padding: 4 }}>
            {ownerGroups.length === 0 ? (
              <div style={{ background: pal.cardBg, borderRadius: 12, padding: 40, textAlign: 'center', color: pal.tileLabel }}>No incidents in this period.</div>
            ) : (
              <>
                <div className="dr-no-print" style={{ padding: '0 4px 16px' }}>
                  <select
                    value={effectiveAnalyst}
                    onChange={(e) => setSelectedAnalyst(e.target.value)}
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-bold"
                  >
                    {ownerGroups.map((g) => (
                      <option key={g.owner} value={g.owner}>{g.owner} ({g.totalBreached} breach{g.totalBreached === 1 ? '' : 'es'})</option>
                    ))}
                  </select>
                </div>
                {selectedGroup && (
                  <IndividualReportCard
                     key={selectedGroup.owner}
                    pal={pal}
                    group={selectedGroup}
                    logoUrl={dailyReport.logoUrl}
                    customerName={dailyReport.customerName}
                    periodLabel={periodLabel}
                    from={dailyReport.from}
                    to={dailyReport.to}
                    generatedAt={dailyReport.generatedAt}
                    customerTz={customerTz}
                  />
                )}
              </>
            )}
          </div>
        ) : null
      ) : isDaily && dailyReport ? (
        <div className="soc-kpi-report-root" style={{ background: pal.pageBg, borderRadius: 16, padding: 4 }}>
          <div style={{ background: pal.cardBg, borderRadius: 12, overflow: 'hidden', fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 8px' }}>
            {dailyReport.logoUrl ? (
                <img src={dailyReport.logoUrl} alt={dailyReport.customerName} style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} />
              ) : <div />}
              <img src="/kloudynet-logo.png?v=2" alt="Kloudynet" style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>

            <div style={{ textAlign: 'center', padding: '8px 24px 24px' }}>
              <div style={{ color: pal.blue, fontSize: 24, fontWeight: 800 }}>SECURITY OPERATIONS REPORT</div>
              <div style={{ color: pal.customerName, fontSize: 16, marginTop: 8 }}>{dailyReport.customerName}</div>
              <div style={{ color: pal.periodText, fontSize: 12, marginTop: 4 }}>Report Generated: {formatInTz(dailyReport.generatedAt, customerTz, { dateStyle: 'medium', timeStyle: 'short' })}</div>
            </div>

            <div style={{ height: 3, background: `linear-gradient(90deg, ${pal.blue}, ${pal.orange})` }} />

            <div style={{ margin: '24px', overflowX: 'auto' }}>
              <IncidentTable pal={pal} incidents={dailyReport.incidents} />
            </div>

            <div style={{ margin: '0 24px 16px', background: pal.footerNoteBg, borderRadius: 6, padding: '12px 16px', color: pal.footerNoteText, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Report Summary:</div>
              <div>Reporting Period: {formatInTz(dailyReport.from, customerTz, { dateStyle: 'medium', timeStyle: 'short' })} - {fmtRangeEnd(dailyReport.to, customerTz)}</div>
              <div style={{ fontWeight: 700, marginTop: 10, marginBottom: 4 }}>Metric Definitions:</div>
              <div>MTTD (Mean Time to Detect): Time from first activity to incident creation</div>
              <div>MTTR (Mean Time to Respond): Time from incident creation to closure</div>
            </div>

            <div style={{ margin: '0 24px 24px', overflowX: 'auto' }}>
              <div style={{ color: pal.footerNoteText, fontSize: 12, fontWeight: 700, marginBottom: 6 }}>⏱️ SLA Thresholds:</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#1e5fa8' }}>
                    {['Priority', 'MTTD (Detection)', 'MTTR (Response)'].map((h) => (
                      <th key={h} style={{ color: '#fff', padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyReport.thresholds.map((th, i) => (
                    <tr key={th.severity} style={{ background: i % 2 === 0 ? pal.cardBg : pal.cardBg2 }}>
                        <td style={{ color: pal.ownerText, padding: '8px 10px', fontWeight: 600 }}>{th.severity}</td>
                        <td style={{ color: pal.neutralCell, padding: '8px 10px' }}>{fmtMinutes(th.ttaMinutes)}</td>
                         <td style={{ color: pal.neutralCell, padding: '8px 10px' }}>{fmtMinutes(th.ttrMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ textAlign: 'center', padding: '16px', borderTop: `1px solid ${pal.copyrightBorder}`, color: pal.copyrightText, fontSize: 11 }}>
              © {new Date(dailyReport.generatedAt).getFullYear()} {dailyReport.customerName} | Managed by Kloudynet Technologies Sdn Bhd
            </div>
          </div>
        </div>
      ) : !isDaily && report ? (
        <div className="soc-kpi-report-root" style={{ background: pal.pageBg, borderRadius: 16, padding: 4 }}>
          <div style={{ background: pal.cardBg, borderRadius: 12, overflow: 'hidden', fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 8px' }}>
              {report.logoUrl ? (
                <img src={report.logoUrl} alt={report.customerName} style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} />
              ) : <div />}
              <img src="/kloudynet-logo.png?v=2" alt="Kloudynet" style={{ height: 56, background: pal.logoCardBg, padding: 8, borderRadius: 8 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>

            <div style={{ textAlign: 'center', padding: '8px 24px 24px' }}>
              <div style={{ color: pal.headerLabel, fontSize: 13, fontWeight: 700, letterSpacing: 3 }}>SECURITY OPERATIONS CENTER</div>
              <div style={{ color: pal.orange, fontSize: 24, fontWeight: 800, marginTop: 6 }}>{periodTitle} SLA Breach Summary</div>
              <div style={{ color: pal.customerName, fontSize: 14, marginTop: 8 }}>{report.customerName.toUpperCase()}</div>
              <div style={{ color: pal.periodText, fontSize: 12, marginTop: 4 }}>
                Report Period: {formatInTz(report.from, customerTz, { dateStyle: 'medium', timeStyle: 'short' })} - {fmtRangeEnd(report.to, customerTz)}
              </div>
            </div>

            <div style={{ height: 3, background: `linear-gradient(90deg, ${pal.orange}, ${pal.red})` }} />

            <div style={{ display: 'flex', gap: 16, padding: 24 }}>
              <div style={{ flex: 1, background: pal.cardBg2, borderLeft: `4px solid ${pal.red}`, borderRadius: 8, padding: '18px 20px' }}>
                <div style={{ color: pal.red, fontSize: 32, fontWeight: 800 }}>{report.totalOutsideSla}</div>
                <div style={{ color: pal.tileLabel, fontSize: 12, marginTop: 4 }}>Total Outside SLA</div>
              </div>
              <div style={{ flex: 1, background: pal.cardBg2, borderLeft: `4px solid ${pal.orange}`, borderRadius: 8, padding: '18px 20px' }}>
                <div style={{ color: pal.orange, fontSize: 32, fontWeight: 800 }}>{report.ownersWithBreaches}</div>
                <div style={{ color: pal.tileLabel, fontSize: 12, marginTop: 4 }}>Owners with Breaches</div>
              </div>
            </div>

            <div style={{ margin: '0 24px 12px', background: pal.bannerBg, borderRadius: 6, padding: '10px 16px', color: pal.bannerText, fontSize: 12, fontWeight: 600 }}>
              OWNER ACCOUNTABILITY: Breach count per analyst, sorted by highest first.
            </div>

            <div style={{ margin: '0 24px 24px', overflowX: 'auto' }}>
              {report.owners.length === 0 ? (
                <div style={{ color: pal.tileLabel, fontSize: 13, padding: 24, textAlign: 'center' }}>No SLA breaches in this period.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#1e5fa8' }}>
                      {['Owner', 'Total Breached', 'Critical', 'High', 'Medium', 'Low', 'Info', 'MTTD Breaches', 'MTTR Breaches', 'Avg MTTD', 'Avg MTTR'].map((h) => (
                        <th key={h} style={{ color: '#fff', padding: '10px 8px', textAlign: h === 'Owner' ? 'left' : 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.owners.map((o, i) => (
                      <tr key={o.owner} style={{ background: i % 2 === 0 ? pal.cardBg : pal.cardBg2 }}>
                        <td style={{ color: pal.ownerText, padding: '8px', fontWeight: 600 }}>{o.owner}</td>
                        <td style={{ color: pal.red, padding: '8px', textAlign: 'center', fontWeight: 800 }}>{o.totalBreached}</td>
                        <td style={{ color: '#f87171', padding: '8px', textAlign: 'center' }}>{o.criticalBreaches}</td>
                        <td style={{ color: '#fb923c', padding: '8px', textAlign: 'center' }}>{o.highBreaches}</td>
                        <td style={{ color: '#d97706', padding: '8px', textAlign: 'center' }}>{o.mediumBreaches}</td>
                        <td style={{ color: '#3b82f6', padding: '8px', textAlign: 'center' }}>{o.lowBreaches}</td>
                        <td style={{ color: pal.infoCell, padding: '8px', textAlign: 'center' }}>{o.informationalBreaches}</td>
                        <td style={{ color: pal.neutralCell, padding: '8px', textAlign: 'center' }}>{o.mttdBreaches}</td>
                        <td style={{ color: pal.neutralCell, padding: '8px', textAlign: 'center' }}>{o.mttrBreaches}</td>
                        <td style={{ color: pal.red, padding: '8px', textAlign: 'center', fontWeight: 700 }}>{fmtMinutes(o.avgMttdMinutes)}</td>
                        <td style={{ color: pal.red, padding: '8px', textAlign: 'center', fontWeight: 700 }}>{fmtMinutes(o.avgMttrMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ margin: '0 24px 20px', background: pal.footerNoteBg, borderRadius: 6, padding: '10px 16px', color: pal.footerNoteText, fontSize: 11 }}>
              <strong>Total Breached:</strong> Incidents where MTTD/MTTR exceeded SLA &nbsp;|&nbsp; <strong>Avg MTTD/MTTR:</strong> Average time in minutes for breached incidents
            </div>

            <div style={{ textAlign: 'center', padding: '16px', borderTop: `1px solid ${pal.copyrightBorder}`, color: pal.copyrightText, fontSize: 11 }}>
              © {new Date(report.generatedAt).getFullYear()} {report.customerName} | Managed by Kloudynet Technologies Sdn Bhd
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
